import {
	AlertCircle,
	AlertTriangle,
	CheckCircle2,
	Flag,
	type LucideIcon,
} from "lucide-react";
import { useGoalStatusStyle } from "web_myplann/Goals/Hooks/useGoalStatusStyle";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	GOAL_CATEGORY_CONFIG,
	type StatusStyle,
} from "web_myplann/Goals/Types/GoalTypes";
import { GoalCoveredStateEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

const STATUS_ICONS: Record<GoalCoveredStateEnum, LucideIcon> = {
	[GoalCoveredStateEnum.OnTrack]: CheckCircle2,
	[GoalCoveredStateEnum.Warning]: AlertCircle,
	[GoalCoveredStateEnum.OffTrack]: AlertTriangle,
	[GoalCoveredStateEnum.Unplanned]: Flag,
};

const STATUS_LABEL_KEYS: Record<
	GoalCoveredStateEnum,
	typeof Resources.Goals.Detail.Status.onTrack
> = {
	[GoalCoveredStateEnum.OnTrack]: Resources.Goals.Detail.Status.onTrack,
	[GoalCoveredStateEnum.Warning]: Resources.Goals.Detail.Status.warning,
	[GoalCoveredStateEnum.OffTrack]: Resources.Goals.Detail.Status.offTrack,
	[GoalCoveredStateEnum.Unplanned]: Resources.Goals.Detail.Status.unplanned,
};

export type UseGoalDetailHeroResult = {
	categoryConfig: (typeof GOAL_CATEGORY_CONFIG)[keyof typeof GOAL_CATEGORY_CONFIG];
	CategoryIcon: LucideIcon;
	statusStyle: StatusStyle;
	StatusIcon: LucideIcon | null;
	statusLabel: string;
	coveredAmount: number;
	progressPct: number;
};

export const useGoalDetailHero = (
	goal: GoalDetail,
): UseGoalDetailHeroResult => {
	const { t } = useResource();

	const categoryConfig = GOAL_CATEGORY_CONFIG[goal.category];
	const CategoryIcon = categoryConfig.icon;
	const statusStyle = useGoalStatusStyle(goal.status);
	const StatusIcon = (goal.status && STATUS_ICONS[goal.status]) || null;
	const statusLabelResource = goal.status && STATUS_LABEL_KEYS[goal.status];
	const statusLabel = statusLabelResource ? t(statusLabelResource) : "";

	const progressPct =
		goal.savingsTarget > 0
			? Math.min(
					100,
					Math.round((goal.coveredAmount / goal.savingsTarget) * 100),
				)
			: 0;

	return {
		categoryConfig,
		CategoryIcon,
		statusStyle,
		StatusIcon,
		statusLabel,
		coveredAmount: goal.coveredAmount,
		progressPct,
	};
};
